import { lazy, Suspense, useEffect, useRef } from "react";
import { Navigate, Route, Routes } from "react-router";
import { AdminRoute } from "./components/AdminRoute";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FeatureFlagRoute } from "./components/FeatureFlagRoute";
import { AppShellSkeleton } from "./components/LoadingSkeleton";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RequireLLMAccess } from "./components/RequireLLMAccess";
import { RouteAnnouncer } from "./components/RouteAnnouncer";
import { LandingPage } from "./features/landing/LandingPage";
import { analytics } from "./lib/shared/analytics";
import { useAuth } from "./lib/shared/auth";

/* ── Lazy-loaded route components ─────────────────────────────────── */

const HomePage = lazy(() =>
  import("./features/home/HomePage").then((m) => ({ default: m.HomePage })),
);
const VaultPage = lazy(() =>
  import("./features/vault/VaultPage").then((m) => ({ default: m.VaultPage })),
);
const MiningPage = lazy(() =>
  import("./features/mining/MiningPage").then((m) => ({
    default: m.MiningPage,
  })),
);
const JobsPage = lazy(() =>
  import("./features/jobs/JobsPage").then((m) => ({ default: m.JobsPage })),
);
const JobDetailPage = lazy(() =>
  import("./features/jobs/JobDetailPage").then((m) => ({
    default: m.JobDetailPage,
  })),
);
const SwipePage = lazy(() =>
  import("./features/swipe/SwipePage").then((m) => ({ default: m.SwipePage })),
);
const TrackerPage = lazy(() =>
  import("./features/tracker/TrackerPage").then((m) => ({
    default: m.TrackerPage,
  })),
);
const SettingsPage = lazy(() =>
  import("./features/settings/SettingsPage").then((m) => ({
    default: m.SettingsPage,
  })),
);
const ProUpgradePage = lazy(() =>
  import("./features/settings/ProUpgrade").then((m) => ({
    default: m.ProUpgrade,
  })),
);
const ProfilePage = lazy(() =>
  import("./features/profile/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  })),
);
const ResumePage = lazy(() =>
  import("./features/resume/ResumePage").then((m) => ({
    default: m.ResumePage,
  })),
);
const ResumeSection = lazy(() =>
  import("./features/resume/ResumeSection").then((m) => ({
    default: m.ResumeSection,
  })),
);
const ResumeLibraryPage = lazy(() =>
  import("./features/resume/ResumeLibraryPage").then((m) => ({
    default: m.ResumeLibraryPage,
  })),
);
const CompanyProfilePage = lazy(() =>
  import("./features/companies/CompanyProfilePage").then((m) => ({
    default: m.CompanyProfilePage,
  })),
);
const PrivacyPolicyPage = lazy(() =>
  import("./features/privacy/PrivacyPolicyPage").then((m) => ({
    default: m.PrivacyPolicyPage,
  })),
);
const TermsOfServicePage = lazy(() =>
  import("./features/terms/TermsOfServicePage").then((m) => ({
    default: m.TermsOfServicePage,
  })),
);
const SubProcessorsPage = lazy(() =>
  import("./features/legal/SubProcessorsPage").then((m) => ({
    default: m.SubProcessorsPage,
  })),
);
const TakedownRequestPage = lazy(() =>
  import("./features/takedown/TakedownRequestPage").then((m) => ({
    default: m.TakedownRequestPage,
  })),
);
const TrustPage = lazy(() =>
  import("./features/trust/TrustPage").then((m) => ({
    default: m.TrustPage,
  })),
);
const BetaAccessPage = lazy(() =>
  import("./features/beta/BetaAccessPage").then((m) => ({
    default: m.BetaAccessPage,
  })),
);
const LoginPage = lazy(() =>
  import("./features/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazy(() =>
  import("./features/auth/SignupPage").then((m) => ({ default: m.SignupPage })),
);
const AuthCallbackPage = lazy(() =>
  import("./features/auth/AuthCallbackPage").then((m) => ({
    default: m.AuthCallbackPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import("./features/auth/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("./features/auth/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const MeSection = lazy(() =>
  import("./features/me/MeSection").then((m) => ({ default: m.MeSection })),
);
const JobsSection = lazy(() =>
  import("./features/jobs/JobsSection").then((m) => ({
    default: m.JobsSection,
  })),
);
const AdminLandingPage = lazy(() =>
  import("./features/admin/AdminLandingPage").then((m) => ({
    default: m.AdminLandingPage,
  })),
);
const RefreshStatusPage = lazy(() =>
  import("./features/admin/RefreshStatusPage").then((m) => ({
    default: m.RefreshStatusPage,
  })),
);
const DemoUsagePage = lazy(() =>
  import("./features/admin/DemoUsagePage").then((m) => ({
    default: m.DemoUsagePage,
  })),
);
const UserOpsPage = lazy(() =>
  import("./features/admin/UserOpsPage").then((m) => ({
    default: m.UserOpsPage,
  })),
);
const ObservabilityDashboardPage = lazy(() =>
  import("./features/admin/ObservabilityDashboardPage").then((m) => ({
    default: m.ObservabilityDashboardPage,
  })),
);
const SkillReviewPage = lazy(() =>
  import("./features/admin/SkillReviewPage").then((m) => ({
    default: m.SkillReviewPage,
  })),
);
const BetaOpsPage = lazy(() =>
  import("./features/admin/BetaOpsPage").then((m) => ({
    default: m.BetaOpsPage,
  })),
);
const FunnelDashboardPage = lazy(() =>
  import("./features/admin/analytics/FunnelDashboardPage").then((m) => ({
    default: m.FunnelDashboardPage,
  })),
);
const GrowthDashboardPage = lazy(() =>
  import("./features/admin/analytics/GrowthDashboardPage").then((m) => ({
    default: m.GrowthDashboardPage,
  })),
);
const FeatureAdoptionDashboard = lazy(() =>
  import("./features/admin/analytics/FeatureAdoptionDashboard").then((m) => ({
    default: m.FeatureAdoptionDashboard,
  })),
);
const ApplicationOutcomesPage = lazy(() =>
  import("./features/admin/analytics/ApplicationOutcomesPage").then((m) => ({
    default: m.ApplicationOutcomesPage,
  })),
);
const EngagementDashboardPage = lazy(() =>
  import("./features/admin/analytics/EngagementDashboardPage").then((m) => ({
    default: m.EngagementDashboardPage,
  })),
);
const AiOpsDashboardPage = lazy(() =>
  import("./features/admin/analytics/AiOpsDashboardPage").then((m) => ({
    default: m.AiOpsDashboardPage,
  })),
);
const MiningModeDashboard = lazy(() =>
  import("./features/admin/analytics/MiningModeDashboard").then((m) => ({
    default: m.MiningModeDashboard,
  })),
);
const TractionExportPage = lazy(() =>
  import("./features/admin/analytics/TractionExportPage").then((m) => ({
    default: m.TractionExportPage,
  })),
);
const TractionSharePage = lazy(() =>
  import("./features/admin/analytics/TractionSharePage").then((m) => ({
    default: m.TractionSharePage,
  })),
);
const ExperimentsDashboardPage = lazy(() =>
  import("./features/admin/analytics/ExperimentsDashboardPage").then((m) => ({
    default: m.ExperimentsDashboardPage,
  })),
);
const DiscoverPage = lazy(() =>
  import("./features/discover/DiscoverPage").then((m) => ({
    default: m.DiscoverPage,
  })),
);
const QuickImportPage = lazy(() =>
  import("./features/quick-import/QuickImportPage").then((m) => ({
    default: m.QuickImportPage,
  })),
);
/* BetaAgreementPage removed — beta agreement is now a soft modal triggered
   on first AI use, not a hard gate after signup. See BetaAgreementGateProvider. */
const PracticeSession = lazy(() =>
  import("./features/practice/PracticeSession").then((m) => ({
    default: m.PracticeSession,
  })),
);
const NotFoundPage = lazy(() =>
  import("./features/not-found/NotFoundPage").then((m) => ({
    default: m.NotFoundPage,
  })),
);
const InLayoutNotFoundPage = lazy(() =>
  import("./features/not-found/NotFoundPage").then((m) => ({
    default: m.InLayoutNotFoundPage,
  })),
);
const SharedSkillGraphPage = lazy(() =>
  import("./features/share/SharedSkillGraphPage").then((m) => ({
    default: m.SharedSkillGraphPage,
  })),
);
const DocumentsPage = lazy(() =>
  import("./features/documents/DocumentsPage").then((m) => ({
    default: m.DocumentsPage,
  })),
);
const ProposalsReviewPage = lazy(() =>
  import("./features/proposals/ProposalsReviewPage").then((m) => ({
    default: m.ProposalsReviewPage,
  })),
);
const LensesPage = lazy(() =>
  import("./features/lenses/LensesPage").then((m) => ({
    default: m.LensesPage,
  })),
);
const VaultHistoryPage = lazy(() =>
  import("./features/vault-history/VaultHistoryPage").then((m) => ({
    default: m.VaultHistoryPage,
  })),
);
const McpDocsPage = lazy(() =>
  import("./features/mcp/McpDocsPage").then((m) => ({
    default: m.McpDocsPage,
  })),
);
const OAuthConsentPage = lazy(() =>
  import("./features/oauth/ConsentPage").then((m) => ({
    default: m.ConsentPage,
  })),
);
/* Public attestation pages (provenance ladder rung 2) — attesters are
   not Traitprint users, so these live outside every auth wrapper. */
const AttestCodeEntryPage = lazy(() =>
  import("./features/attestations/AttestCodeEntryPage").then((m) => ({
    default: m.AttestCodeEntryPage,
  })),
);
const AttestRequestPage = lazy(() =>
  import("./features/attestations/AttestRequestPage").then((m) => ({
    default: m.AttestRequestPage,
  })),
);
const AttestDonePage = lazy(() =>
  import("./features/attestations/AttestDonePage").then((m) => ({
    default: m.AttestDonePage,
  })),
);
const AttestManagePage = lazy(() =>
  import("./features/attestations/AttestManagePage").then((m) => ({
    default: m.AttestManagePage,
  })),
);

/* ── Auth route transition wrapper + app shell ────────────────────── */
// Both are lazy so the entry chunk stays framer-motion-free: the anonymous
// landing route (`/`) must not pay for the authenticated shell (Layout pulls
// framer-motion, gamification animations, and the matching lib via
// NotificationBell — ~70 kB gzip that never renders for guests).

const AuthLayout = lazy(() =>
  import("./components/AuthLayout").then((m) => ({ default: m.AuthLayout })),
);
const Layout = lazy(() => import("./components/Layout").then((m) => ({ default: m.Layout })));

/* ── Root route: landing for guests, redirect to the authenticated home route ── */

function LandingGate() {
  const { user, loading } = useAuth();
  if (loading) {
    return <AppShellSkeleton />;
  }
  if (!user) return <LandingPage />;
  return <Navigate to="/home" replace />;
}

export function App() {
  const sessionTracked = useRef(false);
  useEffect(() => {
    if (!sessionTracked.current) {
      sessionTracked.current = true;
      analytics.engagement.sessionStart();
    }
  }, []);

  return (
    <ErrorBoundary>
      <RouteAnnouncer />
      <ChunkErrorBoundary>
        <Suspense fallback={<AppShellSkeleton />}>
          <Routes>
            <Route index element={<LandingGate />} />
            <Route
              path="/share/graph/:userId"
              element={
                <ErrorBoundary>
                  <SharedSkillGraphPage />
                </ErrorBoundary>
              }
            />
            <Route path="/takedown" element={<TakedownRequestPage />} />
            <Route path="/trust" element={<TrustPage />} />
            <Route path="/beta" element={<BetaAccessPage />} />
            <Route path="/mcp" element={<McpDocsPage />} />
            {/* Public attestation flow — static paths before the :token param */}
            <Route path="/attest" element={<AttestCodeEntryPage />} />
            <Route path="/attest/done" element={<AttestDonePage />} />
            <Route path="/attest/manage" element={<AttestManagePage />} />
            <Route path="/attest/:token" element={<AttestRequestPage />} />
            {/* OAuth 2.1 consent (C1) — standalone: handles its own auth
                redirect and must keep its query string out of the app shell */}
            <Route path="/oauth/consent" element={<OAuthConsentPage />} />
            <Route element={<AuthLayout />}>
              <Route path="/auth/login" element={<LoginPage />} />
              <Route path="/auth/signup" element={<SignupPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/privacy-public" element={<PrivacyPolicyPage />} />
              <Route path="/sub-processors" element={<SubProcessorsPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
            </Route>
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route
                path="home"
                element={
                  <ErrorBoundary>
                    <HomePage />
                  </ErrorBoundary>
                }
              />
              <Route path="me" element={<MeSection />}>
                <Route
                  index
                  element={
                    <ErrorBoundary>
                      <ProfilePage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="vault"
                  element={
                    <ErrorBoundary>
                      <VaultPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="vault/history"
                  element={
                    <ErrorBoundary>
                      <VaultHistoryPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="documents"
                  element={
                    <ErrorBoundary>
                      <DocumentsPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="proposals"
                  element={
                    <ErrorBoundary>
                      <ProposalsReviewPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="lenses"
                  element={
                    <ErrorBoundary>
                      <LensesPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="mining"
                  element={
                    <ErrorBoundary>
                      <RequireLLMAccess>
                        <MiningPage />
                      </RequireLLMAccess>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="resume"
                  element={
                    <ErrorBoundary>
                      <RequireLLMAccess>
                        <ResumeSection />
                      </RequireLLMAccess>
                    </ErrorBoundary>
                  }
                >
                  <Route
                    index
                    element={
                      <ErrorBoundary>
                        <ResumePage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="library"
                    element={
                      <ErrorBoundary>
                        <ResumeLibraryPage />
                      </ErrorBoundary>
                    }
                  />
                </Route>
                <Route path="*" element={<InLayoutNotFoundPage />} />
              </Route>
              <Route
                path="setup/quick-import"
                element={
                  <ErrorBoundary>
                    <RequireLLMAccess>
                      <QuickImportPage />
                    </RequireLLMAccess>
                  </ErrorBoundary>
                }
              />
              <Route path="jobs" element={<JobsSection />}>
                <Route
                  index
                  element={
                    <ErrorBoundary>
                      <JobsPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path=":jobId"
                  element={
                    <ErrorBoundary>
                      <JobDetailPage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path=":jobId/practice"
                  element={
                    <ErrorBoundary>
                      <FeatureFlagRoute flag="practice">
                        <RequireLLMAccess>
                          <PracticeSession />
                        </RequireLLMAccess>
                      </FeatureFlagRoute>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="discover"
                  element={
                    <ErrorBoundary>
                      <FeatureFlagRoute flag="discover">
                        <DiscoverPage />
                      </FeatureFlagRoute>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="swipe"
                  element={
                    <ErrorBoundary>
                      <SwipePage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="tracker"
                  element={
                    <ErrorBoundary>
                      <TrackerPage />
                    </ErrorBoundary>
                  }
                />
              </Route>
              <Route
                path="companies/:companyId"
                element={
                  <ErrorBoundary>
                    <CompanyProfilePage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="admin"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <AdminLandingPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/refresh"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <RefreshStatusPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/observability"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <ObservabilityDashboardPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/users"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <UserOpsPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/beta"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <BetaOpsPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/demo"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <DemoUsagePage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/skill-review"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <SkillReviewPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/funnel"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <FunnelDashboardPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/growth"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <GrowthDashboardPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/features"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <FeatureAdoptionDashboard />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/applications"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <ApplicationOutcomesPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/engagement"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <EngagementDashboardPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/ai-ops"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <AiOpsDashboardPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/mining-modes"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <MiningModeDashboard />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/traction"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <TractionExportPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="admin/analytics/experiments"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <ExperimentsDashboardPage />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="traction/:token"
                element={
                  <ErrorBoundary>
                    <TractionSharePage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="settings"
                element={
                  <ErrorBoundary>
                    <SettingsPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="settings/upgrade"
                element={
                  <ErrorBoundary>
                    <ProUpgradePage />
                  </ErrorBoundary>
                }
              />
              <Route path="privacy" element={<PrivacyPolicyPage />} />
            </Route>
            {/* Top-level catch-all renders the public 404 page so unauthenticated
                visitors hitting unknown URLs see "page not found" instead of
                being silently redirected to the login form. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </ErrorBoundary>
  );
}
